import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Field, FormRenderProps, useField } from 'react-final-form';

import type PageBaseProps from 'types/PageBase';

import Modal from 'components/ui/Modal';
import PageFooter from 'components/ui/PageFooter';
import RadioBullet from 'components/ui/RadioBullet';
import Button, { ButtonType } from 'components/ui/Button';
import { UPDATE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';
import { getIsAdditionalCustomerDriving, isAdditionalCustomerQuestionEnabled, isBreakdownSelfService } from 'modules/selfServices/selectors';
import { useUpdateSelfServiceMutation } from 'modules/selfServices/service';

import ContactForm, { ContactFormProps } from './ContactForm';
import { useSelector } from '../../hooks';

type CustomerInfoProps = {
  isLoading: boolean;
  isAdditionalCustomer?: boolean;
} & PageBaseProps & FormRenderProps & Pick<ContactFormProps, 'isPhoneNumberOrEmailMandatory'>;

const Content: React.FC<CustomerInfoProps> = ({
  handleSubmit,
  pristine,
  onNext,
  onPrev,
  isLoading,
  errors,
  isPhoneNumberOrEmailMandatory,
  shouldDisplayBackButton,
  isPenultimate,
  isAdditionalCustomer,
}) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const isBreakdown = useSelector(isBreakdownSelfService);
  const additionalCustomerQuestionEnabled = useSelector(isAdditionalCustomerQuestionEnabled);
  const isAdditionalCustomerDriving = useSelector(getIsAdditionalCustomerDriving);

  const [displayPersistModal, setDisplayPersistModal] = React.useState(false);
  const { value: permanent } = useField('permanent', { subscription: { value: true }, initialValue: String(!isBreakdown) }).input;
  const [phoneFormatError, setPhoneFormatError] = React.useState(false);
  const [, { isLoading: isUpdateLoading }] = useUpdateSelfServiceMutation({ fixedCacheKey: UPDATE_FIXED_CACHE_KEY });

  const handleCancel = React.useCallback(() => setDisplayPersistModal(false), []);
  const handleNext = React.useCallback(
    () => {
      if (pristine) {
        return onNext?.();
      }
      if (isBreakdown || isAdditionalCustomer) {
        return handleSubmit();
      }
      return setDisplayPersistModal(true);
    },
    [handleSubmit, isAdditionalCustomer, isBreakdown, onNext, pristine],
  );

  const handleError = React.useCallback((isError: boolean) => {
    setPhoneFormatError(isError);
  }, []);

  const isYesNoUnanswered = !isAdditionalCustomer && additionalCustomerQuestionEnabled && isAdditionalCustomerDriving === undefined;
  const hasErrors = phoneFormatError || Object.keys(errors).length > 0 || isYesNoUnanswered;

  return (
    <>
      <div className="main-content">
        <main>
          <p className="text-center">
            {!isAdditionalCustomer && <FormattedMessage
              id="page.contactInfo.subtitle"
              defaultMessage="Check your contact details and modify them if necessary."
            />}
            {isAdditionalCustomer && <FormattedMessage
              id="page.additionalContactInfo.subtitle"
              defaultMessage="Please enter the details of the person dropping off the vehicle."
            />}
          </p>
          <div className="content">
            <ContactForm onError={handleError}
              isPhoneNumberOrEmailMandatory={isPhoneNumberOrEmailMandatory}
              isAdditionalCustomer={isAdditionalCustomer} />
          </div>
        </main>
        <PageFooter
          onPrev={onPrev}
          onNext={handleNext}
          disabled={hasErrors}
          loading={isLoading || isUpdateLoading}
          isPenultimate={isPenultimate}
          shouldDisplayBackButton={shouldDisplayBackButton}
        />
      </div>
      <Modal
        focusRef={ref}
        open={displayPersistModal}
        className="modal-large"
        onClose={setDisplayPersistModal}
        title={(
          <FormattedMessage
            id="page.contactInfo.persistModal.title"
            defaultMessage="How would you like to save your information?"
            tagName="h2"
          />
        )}
      >
        <div
          className="flex flex-col items-center mt-5 kiosk:mt-10"
          data-testid="persistModal"
        >

          <Field name="permanent" type="radio" value="true">
            {({ input }) => (
              <RadioBullet
                checked={input.checked}
                title={(
                  <FormattedMessage
                    id="page.contactInfo.persistModal.content.permanently.title"
                    defaultMessage="Permanently"
                  />
                )}
                description={(
                  <FormattedMessage
                    id="page.contactInfo.persistModal.content.permanently.description"
                    defaultMessage="Make it my default contact information"
                  />
                )}
                {...input}
              />
            )}
          </Field>
          <Field name="permanent" type="radio" value="false">
            {({ input }) => (
              <RadioBullet
                checked={input.checked}
                title={(
                  <FormattedMessage
                    id="page.contactInfo.persistModal.content.temporary.title"
                    defaultMessage="Temporary"
                  />
                )}
                description={(
                  <FormattedMessage
                    id="page.contactInfo.persistModal.content.temporary.description"
                    defaultMessage="Save it for this appointment only"
                  />
                )}
                {...input}
              />
            )}
          </Field>

          <div className="flex flex-col w-full">
            <Button
              analytics={{ category: 'Update', action: 'Update customer info', label: `${permanent ? 'Permanently' : 'Temporarily'}` }}
              loading={isLoading}
              onClick={handleSubmit}
              className="w-full my-xs"
              disabled={isLoading}
              testId="persistCustomerInfo"
            >
              <FormattedMessage id="confirm.title" defaultMessage="Confirm" />
            </Button>
            <Button onClick={handleCancel} type={ButtonType.SECONDARY} className="w-full" testId="cancel-button">
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Content;
